import { If, VLazyLoadMultiSelect, VSelectOptionWithDescription } from '@vision/ui/components';
import { useSelectedAccountId } from '@vision/ui/hooks';
import { useTagSelection } from '@vision/ui/hooks/useTagSelection';
import type { AccountTag } from '@vision/ui/interfaces/account-tag';
import { filterTagIdsByGroups } from '@vision/ui/utils/business/tag.utils';
import { ReportsDetailGraphicSchemaValues, TagGroup } from '@vision/ui/interfaces';
import { useLazyGetTagGroupsQuery, useLazyFetchTagWithPaginationQuery } from '@vision/ui/services';
import { translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportsDetailGraphicCompareLayout } from '../ReportsDetailGraphicCompareLayout';
import { ReportsDetailGraphicBaseProps } from '../report-detail-graphic-compare.util';

interface ReportsDetailGraphicCompareTagNameProps extends ReportsDetailGraphicBaseProps {}

export function ReportsDetailGraphicCompareTagName({
  compareItemType,
  required,
}: ReportsDetailGraphicCompareTagNameProps) {
  const { t } = useTranslation();
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const [loadedTags, setLoadedTags] = useState<AccountTag[]>([]);
  const initialTagIds = useRef(formik.values.selectedCompareCriteria.selectedTagGroupNameIds);

  const { tags: initialTags, isLoading: isLoadingSelectedTags } = useTagSelection({
    nodeId: accountId,
    tagIds: initialTagIds.current,
    includeInternal: true,
  });

  const handleDefaultTagsLoaded = (tags: AccountTag[], requestedTagIds: string[]) => {
    const requestedTagIdSet = new Set(requestedTagIds);
    const loadedTagIds = new Set(tags.map((tag) => tag.id));
    const currentTagIds = formik.values.selectedCompareCriteria.selectedTagGroupNameIds;
    const availableTagIds = currentTagIds.filter((id) => !requestedTagIdSet.has(id) || loadedTagIds.has(id));

    if (availableTagIds.length !== currentTagIds.length) {
      formik.setFieldValue('selectedCompareCriteria.selectedTagGroupNameIds', availableTagIds);
    }
  };

  const handleDefaultTagGroupsLoaded = (groups: TagGroup[], requestedGroupIds: string[]) => {
    const requestedGroupIdSet = new Set(requestedGroupIds);
    const availableGroupIdSet = new Set(groups.map((group) => group.id));
    const currentGroupIds = formik.values.selectedCompareCriteria.selectedTagTagGroupIds;
    const availableGroupIds = currentGroupIds.filter(
      (id) => !requestedGroupIdSet.has(id) || availableGroupIdSet.has(id),
    );

    if (availableGroupIds.length === currentGroupIds.length) return;

    if (availableGroupIds.length > 0) {
      formik.setFieldValue('selectedCompareCriteria.selectedTagTagGroupIds', availableGroupIds);
    } else {
      formik.setValues({
        ...formik.values,
        selectedCompareCriteria: {
          ...formik.values.selectedCompareCriteria,
          selectedTagTagGroupIds: [],
          selectedTagGroupNameIds: [],
        },
      });
    }
  };

  const handleTagGroupChange = (selectedTagGroupValue: string[]) => {
    const newSelectedTagGroupNameIds = selectedTagGroupValue.length
      ? filterTagIdsByGroups(
          [...initialTags, ...loadedTags],
          selectedTagGroupValue,
          formik.values.selectedCompareCriteria.selectedTagGroupNameIds,
        )
      : [];

    formik.setValues({
      ...formik.values,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        selectedTagTagGroupIds: selectedTagGroupValue,
        selectedTagGroupNameIds: newSelectedTagGroupNameIds,
      },
    });
  };

  return (
    <ReportsDetailGraphicCompareLayout title={t(`page-reports:reportCompareGroupItemType.${compareItemType}`)}>
      <VLazyLoadMultiSelect
        id="report-compare-tag-group"
        data-testid="input-report-compare-tag-group"
        label={t('tagGroup')}
        disabled={isLoadingSelectedTags}
        filterKey="tag_group_ids"
        useLazyApiQueryFunction={useLazyGetTagGroupsQuery}
        apiRequestParameters={{ nodeId: accountId, sortBy: 'name', sortOrder: 'asc', includeInternal: true }}
        multiSelectItemMapper={(items) => {
          return items.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        }}
        searchKey="tag_group_name"
        value={formik.values.selectedCompareCriteria.selectedTagTagGroupIds}
        onDefaultValuesLoaded={handleDefaultTagGroupsLoaded}
        onChange={handleTagGroupChange}
        error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedTagTagGroupIds')}
        withAsterisk={required}
      />

      <If value={formik.values.selectedCompareCriteria.selectedTagTagGroupIds.length > 0}>
        <VLazyLoadMultiSelect
          id="report-compare-tag-group-name"
          data-testid="input-report-compare-tag-group-name"
          label={t('tagName')}
          apiRequestParameters={{
            nodeId: accountId,
            tagGroups: formik.values.selectedCompareCriteria.selectedTagTagGroupIds,
            sortBy: 'name',
            sortOrder: 'asc',
            includeInternal: true,
          }}
          useLazyApiQueryFunction={useLazyFetchTagWithPaginationQuery}
          searchKey="tag_name"
          filterKey="ids"
          resetChangeRequestParameters={true}
          multiSelectItemMapper={(items) =>
            items.map((item) => ({ value: item.id, label: item.name, description: item.tagGroupName }))
          }
          itemComponent={VSelectOptionWithDescription}
          value={formik.values.selectedCompareCriteria.selectedTagGroupNameIds}
          searchable={true}
          onDataChange={setLoadedTags}
          onDefaultValuesLoaded={handleDefaultTagsLoaded}
          onChange={(e) => formik.setFieldValue('selectedCompareCriteria.selectedTagGroupNameIds', e)}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedTagGroupNameIds')}
          withAsterisk={required}
        />
      </If>
    </ReportsDetailGraphicCompareLayout>
  );
}
